import React from "react";
import { ExternalLink, Bookmark, TrendingDown, TrendingUp, Package, Zap } from "lucide-react";
import { formatPrice, formatRelative, RETAILER_COLORS } from "@/lib/cr";

function dealColor(score) {
  if (score >= 75) return "text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10";
  if (score >= 50) return "text-[#EAB308] border-[#EAB308]/30 bg-[#EAB308]/10";
  if (score >= 25) return "text-[#A3A3A3] border-[#262626] bg-[#1A1A1A]";
  return "text-[#EF4444] border-[#EF4444]/30 bg-[#EF4444]/10";
}

export default function ListingCard({ listing, onWatch, onQuickCart, quickCartReady }) {
  const retailerColor = RETAILER_COLORS[listing.retailer_id] || "#A3A3A3";
  const discount = listing.msrp ? ((listing.msrp - listing.price) / listing.msrp) * 100 : 0;
  const isDeal = discount > 0;

  return (
    <div
      data-testid={`listing-card-${listing.id}`}
      className="bg-[#111111] border border-[#262626] rounded-sm overflow-hidden flex flex-col card-hover"
    >
      <div className="relative h-32 bg-[#0a0a0a] overflow-hidden">
        {listing.image ? (
          <img
            src={listing.image}
            alt={listing.title}
            className="w-full h-full object-cover opacity-90"
            loading="lazy"
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full text-[#737373]">
            <Package className="w-8 h-8" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <span
            className="text-[10px] font-mono font-bold px-2 py-0.5 uppercase tracking-wider rounded-sm bg-black/80 backdrop-blur-sm border"
            style={{ color: retailerColor, borderColor: `${retailerColor}55` }}
            data-testid={`retailer-badge-${listing.retailer_id}`}
          >
            {listing.retailer_name}
          </span>
          {listing.is_real ? (
            <span
              data-testid={`source-badge-live-${listing.id}`}
              className="text-[10px] font-mono font-bold px-1.5 py-0.5 uppercase tracking-wider rounded-sm bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/40"
              title="Sourced from live retailer API — real product URL"
            >
              LIVE
            </span>
          ) : (
            <span
              data-testid={`source-badge-demo-${listing.id}`}
              className="text-[10px] font-mono font-bold px-1.5 py-0.5 uppercase tracking-wider rounded-sm bg-[#FBBF24]/10 text-[#FBBF24] border border-[#FBBF24]/40"
              title="Simulated listing — link lands on the retailer's category search page"
            >
              DEMO
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          {listing.in_stock ? (
            <span className="bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/30 text-[10px] font-mono px-2 py-0.5 rounded-sm uppercase font-bold">
              In stock
            </span>
          ) : (
            <span className="bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 text-[10px] font-mono px-2 py-0.5 rounded-sm uppercase font-bold">
              Sold out
            </span>
          )}
        </div>
        <div className="absolute bottom-2 right-2">
          <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border rounded-sm ${dealColor(listing.deal_score)}`}>
            DEAL {listing.deal_score}
          </span>
        </div>
      </div>

      <div className="p-3 flex flex-col flex-1 gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold leading-tight line-clamp-2 text-white" title={listing.title}>
            {listing.title}
          </h3>
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#737373] font-mono">
          <span>{listing.game.split(":")[0]}</span>
          <span className="text-[#262626]">•</span>
          <span className="truncate">{listing.set_name}</span>
          <span className="text-[#262626]">•</span>
          <span>{listing.region}</span>
        </div>

        <div className="flex items-end justify-between mt-1">
          <div>
            <div className="font-mono text-xl font-bold text-white tracking-tight">
              {formatPrice(listing.price, listing.currency)}
            </div>
            {listing.msrp && listing.msrp !== listing.price && (
              <div className="flex items-center gap-1 text-[10px] font-mono text-[#737373]">
                <span className="line-through">{formatPrice(listing.msrp)}</span>
                {isDeal ? (
                  <span className="text-[#22C55E] flex items-center gap-0.5">
                    <TrendingDown className="w-3 h-3" />
                    {Math.abs(discount).toFixed(0)}%
                  </span>
                ) : (
                  <span className="text-[#EF4444] flex items-center gap-0.5">
                    <TrendingUp className="w-3 h-3" />
                    {Math.abs(discount).toFixed(0)}%
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="text-right text-[10px] font-mono text-[#737373]">
            {formatRelative(listing.discovered_at)}
            {listing.in_stock && listing.stock_count > 0 && (
              <div>{listing.stock_count} avail.</div>
            )}
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          {quickCartReady && onQuickCart ? (
            <button
              onClick={() => onQuickCart(listing)}
              data-testid={`quick-cart-btn-${listing.id}`}
              title="Open in your browser with the item already in cart, address & card prefilled — review then click Place Order"
              className="flex-1 bg-[#EAB308] text-black font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-sm hover:bg-[#FDE047] transition-all text-center flex items-center justify-center gap-1.5 hover:shadow-[0_0_12px_rgba(234,179,8,0.35)]"
            >
              <Zap className="w-3.5 h-3.5" />
              1-Click cart
            </button>
          ) : (
            <a
              href={listing.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`open-cart-btn-${listing.id}`}
              className="flex-1 bg-[#EAB308] text-black font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-sm hover:bg-[#FDE047] transition-all text-center flex items-center justify-center gap-1.5 hover:shadow-[0_0_12px_rgba(234,179,8,0.35)]"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open cart
            </a>
          )}
          <button
            onClick={() => onWatch && onWatch(listing)}
            data-testid={`watch-btn-${listing.id}`}
            className="px-3 py-2 border border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-[#1A1A1A] hover:border-[#A3A3A3] rounded-sm transition-all"
            title="Add to watchlist"
          >
            <Bookmark className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
